import React, { FC, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	WarehouseDocumentNumberApi,
	WarehouseSettingsApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	stockSettings: WarehouseSettingsApi;
	onSave: (data: WarehouseSettingsApi) => void;
}

const TranslationsInitialized = (date: string) => {
	const { t } = useTranslation();
	t("enums.stock_settings.business_type.BASIC");
	t("enums.stock_settings.business_type.GASTRO");
	t("enums.stock_settings.business_type.WHOLESALE");
	t("enums.stock_settings.stocktaking_constraint_type.STRICT");
	t("enums.stock_settings.stocktaking_constraint_type.WARNING");
	t("enums.stock_settings.stocktaking_constraint_type.LOOSE");
	t("enums.stock_settings.stocktaking_constraint_type.FULL");
	t("enums.stock_settings.states_constraint_type.STRICT");
	t("enums.stock_settings.states_constraint_type.WARNING");
	t("enums.stock_settings.states_constraint_type.LOOSE");
};

const mapDocumentNumbersValues = (documentNumbers: WarehouseDocumentNumberApi) => {
	const values: Record<string, number> = {};
	Object.keys(documentNumbers).forEach((k) => {
		values[k] = generateRandomNum();
	});
	return values;
};

const generateRandomNum = () => Math.floor(1000 + Math.random() * 9000);

const StockSettingsForm: FC<Props> = ({ stockSettings, onSave }) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<WarehouseSettingsApi>({
		criteriaMode: "all",
		defaultValues: stockSettings,
	});
	const {
		setError,
		formState,
		formState: { errors },
		control,
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
	} = form;
	const [loading, setLoading] = useState<boolean>(false);
	const [prevDocumentsValues, setPrevDocumentsValues] = useState<Record<string, number>>(
		mapDocumentNumbersValues(stockSettings.document_numbers)
	);
	const isMobile = useWindowSize().isMobile;

	const stockBusinessTypeOptions = ["BASIC", "GASTRO", "WHOLESALE"].map((opt) => ({
		value: opt,
		label: t(`enums.stock_settings.business_type.${opt}`),
	}));

	const stocktakingConstraintsTypeOptions = ["FULL", "STRICT", "WARNING", "LOOSE"].map((opt) => ({
		value: opt,
		label: t(`enums.stock_settings.stocktaking_constraint_type.${opt}`),
	}));

	const statesConstraintsTypeOptions = ["STRICT", "WARNING", "LOOSE"].map((opt) => ({
		value: opt,
		label: t(`enums.stock_settings.states_constraint_type.${opt}`),
	}));

	const generateNewRandomNumberForKey = (key: string) => {
		setPrevDocumentsValues({
			...prevDocumentsValues,
			[key]: generateRandomNum(),
		});
	};

	const onSubmit = handleSubmit(async (data: WarehouseSettingsApi) => {
		setLoading(true);
		try {
			const res = await api.organization().updateWarehouseSettings(data);
			onSave(res);
			reset(res);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const getShortYear = () => {
		const currentYear = new Date().getFullYear();
		const twoLastDigits = currentYear % 100;
		let formattedTwoLastDigits = "";

		formattedTwoLastDigits += twoLastDigits < 10 ? `0${twoLastDigits}` : twoLastDigits;

		return formattedTwoLastDigits;
	};

	const getDefaultDocumentNumberSuffix = (type: keyof typeof stockSettings.document_numbers) => {
		switch (type) {
			case "purchase_order_prefix":
				return "PO";
			case "purchase_receive_prefix":
				return "PR";
			case "purchase_correction_prefix":
				return "PC";
			case "sales_order_prefix":
				return "SO";
			case "sales_return_prefix":
				return "SR";
			case "sales_ship_prefix":
				return "SHP";
			case "transfer_order_prefix":
				return "TO";
			case "stock_adjustment_prefix":
				return "SA";
			case "bundle_prefix":
				return "BND";
			case "stocktaking_prefix":
				return "ST";
		}
	};

	const getPseudoRandomNumber = (number: number, iterations: number = 1): string => {
		const newNumber = Math.floor((number * 16807) % 2147483647)
			.toString()
			.substring(0, 4);
		if (iterations > 1) {
			return getPseudoRandomNumber(Number(newNumber), iterations - 1);
		}
		return newNumber;
	};

	const drawDocumentNumberSuffix = (type: keyof typeof stockSettings.document_numbers) => {
		let result = watch(`document_numbers.${type}`);
		const number = prevDocumentsValues[type];
		const today = new Date();
		const splittedDate = formatDateToString(today).split("-");

		if (!result) return "";

		if (!result.includes("{num}")) result += "{num}";

		if (result.includes("{year}")) {
			result = result.replaceAll("{year}", `${splittedDate[0]}`);
		}

		if (result.includes("{day_of_month}")) {
			result = result.replaceAll("{day_of_month}", `${splittedDate[2]}`);
		}

		if (result.includes("{month}")) {
			result = result.replaceAll("{month}", `${splittedDate[1]}`);
		}

		if (result.includes(`{yearshort}`)) {
			result = result.replaceAll("{yearshort}", getShortYear());
		}

		let i = 1;
		while (result.includes(`{num}`)) {
			if (i === 1) {
				result = result.replace("{num}", number.toString());
			} else {
				const next = getPseudoRandomNumber(number, i + 1);
				result = result.replace("{num}", next.toString());
			}
			i++;
		}

		return result;
	};

	const onBlurDocumentNumberInput = (type: keyof typeof stockSettings.document_numbers) => {
		const watchedValue = watch(`document_numbers.${type}`);
		if (!watchedValue) {
			const newValue = getDefaultDocumentNumberSuffix(type);
			setValue(`document_numbers.${type}`, newValue, { shouldDirty: true });
			generateNewRandomNumberForKey(type);
		}
	};

	return (
		<FormDirty key="stock-settings-form" loading={loading} formState={formState} noValidate onSubmit={onSubmit}>
			<h5 className={isMobile ? "mt-2" : "mt-0"}>{t("modules.stock_settings.field.basic_settings.title")}</h5>
			<fieldset className="form-group">
				<FormSelectGroup
					control={control}
					name="stock_business_type"
					errors={errors}
					label={t("modules.stock_settings.field.stock_business_type.title")}
					options={stockBusinessTypeOptions}
					data-testid="stock_business_type"
				/>
				<FormSelectGroup
					control={control}
					name="states_constraint_type"
					errors={errors}
					label={t("modules.stock_settings.field.states_constraint_type.title")}
					options={statesConstraintsTypeOptions}
					data-testid="states_constraint_type"
				/>
				<FormSelectGroup
					control={control}
					name="stocktaking_constraint_type"
					errors={errors}
					label={t("modules.stock_settings.field.stocktaking_constraint_type.title")}
					options={stocktakingConstraintsTypeOptions}
					data-testid="stocktaking_constraint_type"
				/>
			</fieldset>
			<h5>{t("modules.stock_settings.field.document_settings.title")}</h5>
			<fieldset className="form-group">
				<Form.Label>{t("modules.stock_settings.field.purchase_order_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.purchase_order_prefix"
						errors={errors}
						onBlur={() => onBlurDocumentNumberInput("purchase_order_prefix")}
						placeholder={getDefaultDocumentNumberSuffix("purchase_order_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("purchase_order_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("purchase_order_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.purchase_receive_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.purchase_receive_prefix"
						errors={errors}
						onBlur={() => onBlurDocumentNumberInput("purchase_receive_prefix")}
						placeholder={getDefaultDocumentNumberSuffix("purchase_receive_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("purchase_receive_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("purchase_receive_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.purchase_correction_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.purchase_correction_prefix"
						errors={errors}
						onBlur={() => onBlurDocumentNumberInput("purchase_correction_prefix")}
						placeholder={getDefaultDocumentNumberSuffix("purchase_correction_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("purchase_correction_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("purchase_correction_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.sales_order_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.sales_order_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("sales_order_prefix")}
						onBlur={() => onBlurDocumentNumberInput("sales_order_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("sales_order_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("sales_order_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.sales_return_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.sales_return_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("sales_return_prefix")}
						onBlur={() => onBlurDocumentNumberInput("sales_return_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("sales_return_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("sales_return_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.sales_ship_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.sales_ship_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("sales_ship_prefix")}
						onBlur={() => onBlurDocumentNumberInput("sales_ship_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("sales_ship_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("sales_ship_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.transfer_order_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.transfer_order_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("transfer_order_prefix")}
						onBlur={() => onBlurDocumentNumberInput("transfer_order_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("transfer_order_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("transfer_order_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.stock_adjustment_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.stock_adjustment_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("stock_adjustment_prefix")}
						onBlur={() => onBlurDocumentNumberInput("stock_adjustment_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("stock_adjustment_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("stock_adjustment_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.bundle_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.bundle_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("bundle_prefix")}
						onBlur={() => onBlurDocumentNumberInput("bundle_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("bundle_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("bundle_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>

				<Form.Label>{t("modules.stock_settings.field.stocktaking_prefix.title")}</Form.Label>
				<InputGroup className="form-input-group">
					<FormInput
						name="document_numbers.stocktaking_prefix"
						errors={errors}
						placeholder={getDefaultDocumentNumberSuffix("stocktaking_prefix")}
						onBlur={() => onBlurDocumentNumberInput("stocktaking_prefix")}
						register={register}
					/>
					{drawDocumentNumberSuffix("stocktaking_prefix") && (
						<InputGroup.Text style={{ height: "31px" }}>
							{drawDocumentNumberSuffix("stocktaking_prefix")}
						</InputGroup.Text>
					)}
				</InputGroup>
			</fieldset>
			<h5>{t("modules.stock_settings.field.advanced_settings.title")}</h5>
			<fieldset className="form-group">
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.stock_settings.field.disable_duplicate_tax_id_no.title")}
					name="disable_duplicate_tax_id_no"
					errors={errors}
				/>
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.stock_settings.field.strict_validate_draft_documents.title")}
					name="strict_validate_draft_documents"
					errors={errors}
				/>
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.stock_settings.field.auto_connect_product_packages.title")}
					name="auto_connect_product_packages"
					errors={errors}
				/>
				<FormNumberInput
					errors={errors}
					name="archive_older_than_days"
					control={control}
					label={t("modules.stock_settings.field.archive_older_than_days.title")}
					suffix=""
				/>
			</fieldset>
		</FormDirty>
	);
};

export default StockSettingsForm;
